import React from 'react';
import Button from '@mui/material/Button';
import DevToolService from '../../services/DevToolService';
import TipContext from '../../providers/GlobalTip';
import CurrentClientContext from '../../providers/CurrentClientProvider';
import CryptoForm from '../../components/crypto/CryptoForm';

const DevTools = () => {
    const [jobStatuses, setJobStatuses] = React.useState({});
    const [jobsRunning, setJobsRunning] = React.useState([]);
    const [locations, setLocations] = React.useState([]);
    const [isLoadingLocations, setIsLoadingLocations] = React.useState(false);
    const [selectedLocationId, setSelectedLocationId] = React.useState('');
    const [selectedPlanAction, setSelectedPlanAction] = React.useState('future_open_plan');
    const [selectedOpenMonth, setSelectedOpenMonth] = React.useState('');
    const currentClientContext = CurrentClientContext.useClient();
    const currentClient = currentClientContext?.client ?? {};
    const currentClientId = currentClient?.Id ?? currentClient?.id;
    const nextMonthMin = React.useMemo(() => {
        const nextMonth = new Date();
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        return `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`;
    }, []);
    const tip_context = TipContext.useMessage()

    React.useEffect(() => {
		document.title = 'Retail ORBIT: Dev Tools'
	}, [])

    React.useEffect(() => {
        const loadLocations = async () => {
            if (!currentClientId) {
                setLocations([]);
                setSelectedLocationId('');
                return;
            }

            setIsLoadingLocations(true);

            try {
                const response = await DevToolService.getPlanToolLocations(currentClientId);
                const nextLocations = response?.locations ?? [];
                setLocations(nextLocations);
                if (nextLocations.length === 0) {
                    setSelectedLocationId('');
                }
            } catch (error) {
                tip_context.show('Error loading locations for plan tools');
                setLocations([]);
                setSelectedLocationId('');
            } finally {
                setIsLoadingLocations(false);
            }
        };

        loadLocations();
    }, [currentClientId, tip_context]);

    const getErrorMessage = (error, fallbackMessage) => {
        return error?.response?.data?.message || error?.message || fallbackMessage;
    };

    /**
     * Run lightspeed populate missing data files for last month.
     * @returns {void}
     */
    const runLightspeedPopulateMissingDataFilesForLastMonth = async () => {
        setJobsRunning((prev) => [...prev, 'lightspeed-last-month-data-files']);
    
        const key = 'lightspeed-last-month-data-files';

        setJobStatuses((prev) => ({
            ...prev,
            [key]: {'status': 'Running', 'message': 'Tool is running...'}
        }));
    
        try {
            const res = await DevToolService.lightspeedPopulateMissingDataFilesForLastMonth();
    
            // Update the state with the new key-value pair
            setJobStatuses((prev) => ({
                ...prev,
                [key]: {'status': 'Success', 'message': res.message}
            }));
        } catch (error) {
            tip_context.show('Error running lightspeed populate missing data files for last month');
    
            // Update the state with the new key-value pair
            setJobStatuses((prev) => ({
                ...prev,
                [key]: {'status': 'Error', 'message': getErrorMessage(error, 'Unknown error')}
            }));
        }
    
        setJobsRunning((prev) => prev.filter((job) => job !== key));
    };

    const runLocationPlanTool = async () => {
        const key = 'location-plan-tool';

        if (!currentClientId) {
            tip_context.show('No active client selected. Select a client and try again.');
            return;
        }

        if (!selectedLocationId) {
            tip_context.show('Please select a location.');
            return;
        }

        if (selectedPlanAction === 'future_open_plan' && !selectedOpenMonth) {
            tip_context.show('Please select a future open month.');
            return;
        }

        setJobsRunning((prev) => [...prev, key]);

        setJobStatuses((prev) => ({
            ...prev,
            [key]: {
                status: 'Running',
                message: 'Tool is running...',
            },
        }));

        try {
            const payload = {
                client_id: currentClientId,
                location_id: Number(selectedLocationId),
                action: selectedPlanAction,
            };

            if (selectedPlanAction === 'future_open_plan') {
                payload.open_month = selectedOpenMonth;
            }

            const res = await DevToolService.runPlanToolAction(payload);

            setJobStatuses((prev) => ({
                ...prev,
                [key]: {
                    status: 'Success',
                    message: res?.message || 'Tool completed successfully.',
                },
            }));
        } catch (error) {
            const message = getErrorMessage(error, 'Error running location plan tool');
            tip_context.show(message);

            setJobStatuses((prev) => ({
                ...prev,
                [key]: {
                    status: 'Error',
                    message,
                },
            }));
        }

        setJobsRunning((prev) => prev.filter((job) => job !== key));
    };

    return (
        <div>
            <h1>Dev Tools</h1>
            <div style={{border: '1px solid #000', padding: '10px', margin: '10px'}}>
                <h2>Populate Missing Data Files for Last Month Lightspeed Clients</h2>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={runLightspeedPopulateMissingDataFilesForLastMonth}
                    disabled={jobsRunning.includes('lightspeed-last-month-data-files')}
                >
                    Run Tool
                </Button>
                <div>
                    {jobStatuses['lightspeed-last-month-data-files'] ?
                        <div>
                            <h3>Status: {jobStatuses['lightspeed-last-month-data-files'].status}</h3>
                            <p>{jobStatuses['lightspeed-last-month-data-files'].message}</p>
                        </div>
                        : null}
                </div>
            </div>
            <div style={{border: '1px solid #000', padding: '10px', margin: '10px'}}>
                <h2>Open Future Plan or Restore Live Plan by Location</h2>
                <div style={{marginBottom: '10px'}}>
                    <label htmlFor="plan-tool-location">Location: </label>
                    <select
                        id="plan-tool-location"
                        value={selectedLocationId}
                        onChange={(event) => setSelectedLocationId(event.target.value)}
                        disabled={isLoadingLocations || !currentClientId || jobsRunning.includes('location-plan-tool')}
                    >
                        <option value="">Select a location</option>
                        {locations.map((location) => (
                            <option key={location.client_location_id} value={location.client_location_id}>
                                {location.location_name} ({location.location_code})
                            </option>
                        ))}
                    </select>
                </div>
                <div style={{marginBottom: '10px'}}>
                    <label htmlFor="plan-tool-action">Action: </label>
                    <select
                        id="plan-tool-action"
                        value={selectedPlanAction}
                        onChange={(event) => setSelectedPlanAction(event.target.value)}
                        disabled={jobsRunning.includes('location-plan-tool')}
                    >
                        <option value="future_open_plan">Future Open Plan</option>
                        <option value="restore_live_plan">Restore Live Plan</option>
                    </select>
                </div>
                {selectedPlanAction === 'future_open_plan' ? (
                    <div style={{marginBottom: '10px'}}>
                        <label htmlFor="plan-tool-open-month">Open Month: </label>
                        <input
                            id="plan-tool-open-month"
                            type="month"
                            value={selectedOpenMonth}
                            min={nextMonthMin}
                            onChange={(event) => setSelectedOpenMonth(event.target.value)}
                            disabled={jobsRunning.includes('location-plan-tool')}
                        />
                        <div style={{fontSize: '12px', marginTop: '4px'}}>
                            Month must be greater than the current month.
                        </div>
                    </div>
                ) : null}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={runLocationPlanTool}
                    disabled={jobsRunning.includes('location-plan-tool') || isLoadingLocations}
                >
                    Run Tool
                </Button>
                <div>
                    {jobStatuses['location-plan-tool'] ? (
                        <div>
                            <h3>Status: {jobStatuses['location-plan-tool'].status}</h3>
                            <p>{jobStatuses['location-plan-tool'].message}</p>
                        </div>
                    ) : null}
                </div>
            </div>
            <div style={{border: '1px solid #000', padding: '10px', margin: '10px'}}>

                <CryptoForm/>
            </div>
            <div data-tf-widget="rF5XHgPz"></div>
            <script src="//embed.typeform.com/next/embed.js"></script>
        </div>
    );
};


export default DevTools;